import { DatePicker, Form, Input, Modal, Switch } from "antd";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ScheduledBillings from "redux/models/scheduledBillings";
import { selectGlobalSelector } from "redux/slices/globalSelectorSlice";
import K from "utilities/constants";
import moment from "moment";
import { columns } from "./clientColumns";
import { ServerSideDataGrid } from "../../../common/components/serverSideDataGrid/serverSideDataGrid";
import { selectUserId, selectUserRoles } from "../../../redux/slices/userSlice";
import { usePageFilter } from "../../../hooks/usePageFilters";
import { DataTableLayout } from "../../../common/components/dataTableLayout/dataTableLayout";

export default function ClientScheduledList() {
  const userId = useSelector(selectUserId);
  const globalSelector = useSelector(selectGlobalSelector);
  const userRoles = useSelector(selectUserRoles);

  const gridRef = useRef(null);
  const editIdRef = useRef(null);
  const [form] = Form.useForm();

  const [search, setSearch] = usePageFilter("client_scheduled_billings_search");
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsModalVisible] = useState(false);

  const editRecord = (data) => {
    setIsModalVisible(true);
    editIdRef.current = data.id;
    form.setFieldsValue({
      duplicateAmount: data.originalDuplicateAmount,
      dueDate: moment(data.dueDate),
      isActive: data.isActive === "Yes",
    });
  };

  const tableColumns = useMemo(
    () => columns(editRecord, userRoles),
    [userRoles],
  );

  const onFinish = async (item) => {
    setIsLoading(true);
    const body = {
      dueDate: item.dueDate.format(K.DateFormat.Display),
      amount: item.duplicateAmount,
      isActive: +item.isActive,
    };
    try {
      const res = await ScheduledBillings.updateClientScheduledBilling(
        body,
        editIdRef.current,
      );
      onConfigUpdated(res.data);

      setIsModalVisible(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getServerSideData = useCallback(
    (options) => {
      return ScheduledBillings.getClientScheduledBillings({
        ...options,
        search: search ?? undefined,
        client: globalSelector.selectedClients,
      });
    },
    [globalSelector.selectedClients, search],
  );

  const onDataFetchFailed = useCallback((err) => console.error(err), []);

  const onConfigUpdated = (config) => {
    gridRef.current.api.applyServerSideTransaction({
      update: [config],
    });

    gridRef.current.api.refreshCells({ force: true });
  };

  return (
    <>
      <DataTableLayout searchDefaultValue={search} onSearchChange={setSearch}>
        <ServerSideDataGrid
          ref={gridRef}
          userId={userId}
          configKey={K.AgGridTable.Keys.ClientScheduledBillingsVisibleColumn}
          getData={getServerSideData}
          defaultColumnConfig={tableColumns}
          defaultColDef={K.AgGridTable.DefaultColDef}
          onDataFetchFailed={onDataFetchFailed}
        />
      </DataTableLayout>

      <Modal
        forceRender
        centered
        open={isVisible}
        closeIcon={<i className="icon-closeable" />}
        okText="Update"
        className="s2-theme-style"
        title="Edit Client Scheduled"
        wrapClassName="vertical-center-modal"
        okButtonProps={{ loading: isLoading }}
        onOk={form.submit}
        onCancel={() => setIsModalVisible(false)}
        afterClose={form.resetFields}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Due Date"
            name="dueDate"
            rules={[{ required: true, message: "Due Date is required." }]}
          >
            <DatePicker format={K.DateFormat.DashUSFormat} />
          </Form.Item>
          <Form.Item
            label="Amount"
            name="duplicateAmount"
            rules={[{ required: true, message: "Amount is required." }]}
          >
            <Input placeholder="Enter amount" />
          </Form.Item>
          <Form.Item label="Active?" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
